import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Box, Typography, TextField, Button, Grid, Autocomplete, 
    Card, CardContent, Paper
} from '@mui/material';
import { getDepartmentWisePendingReport } from '../../redux/actions/reports/reportsAction';
import { getDepartmentWisePendingReport as getDepartmentWisePendingReportApi } from '../../api/reports/reportsApi';
import DepartmentWisePendingReportTable from '../../components/report/DepartmentWisePendingReportTable';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import ReportTitle from '../../components/common/ReportTitle';
import PageLoader from '../../components/common/PageLoader';

// Helper to format data for CSV
import { columns as departmentWisePendingColumns } from '../../components/report/DepartmentWisePendingReportTable';

const formatCsvData = (data) => {
  // Use the columns array from the table for order and selection
  const columns = departmentWisePendingColumns;
  const header = columns.map(col => col.label);
  const rows = data.map(row => {
      return columns.map(col => {
        if (col.key === 'status') return row.status || '';
        if (col.key === 'jo_date') return row.jo_date ? new Date(row.jo_date).toLocaleDateString() : '';
        if (col.key === 'created_at' || col.key === 'processed_at') return row[col.key] ? new Date(row[col.key]).toLocaleString() : '';
        if (col.key === 'net_weight_bip') return row.net_weight !== undefined && row.net_weight !== null ? parseFloat(row.net_weight).toFixed(3) : '';
        if (col.key === 'pure_weight_calc') {
          const netWeight = parseFloat(row.net_weight) || 0;
          const purity = parseFloat(row.hm_purity) || 0;
          return (netWeight * purity / 100).toFixed(3);
        }
        if (col.numeric && row[col.key]) return parseFloat(row[col.key]).toFixed(3);
        return row[col.key] || '';
      });
  });
  return [header.join(','), ...rows.map(r => r.map(val => `"${val ?? ''}"`).join(','))].join('\r\n');
};

const DEFAULT_LIMIT = 200;

const DepartmentWisePendingReportPage = () => {
    const dispatch = useDispatch();
    const { report, loading, error } = useSelector(state => state.departmentWisePendingReport);

    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [workerFilter, setWorkerFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchReport = useCallback((params) => {
        dispatch(getDepartmentWisePendingReport(params));
    }, [dispatch]);

    useEffect(() => {
        fetchReport({ limit: DEFAULT_LIMIT, today_only: true });
    }, [fetchReport]);
    
    const departments = useMemo(() => {
        const deptSet = new Set();
        report.forEach(item => {
            if (item.department_name && item.department_name !== 'Unknown') {
                deptSet.add(item.department_name);
            }
        });
        return Array.from(deptSet).sort();
    }, [report]);

    const workers = useMemo(() => {
        const uniqueWorkers = new Map();
        report.forEach(item => {
            if (item.worker_name && item.worker_name !== 'Unknown' && item.worker_name !== 'Not Assigned') {
                uniqueWorkers.set(item.worker_name, {
                    name: item.worker_name,
                    code: item.worker_code || 'N/A'
                });
            }
        });
        return Array.from(uniqueWorkers.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [report]);

    const filteredReport = useMemo(() => report, [report]);

    // Calculate summary statistics with totals for all statuses
    const summaryStats = useMemo(() => {
        // Processed: only items with status = 'Processed' or batch_no_status = 2
        const processed = filteredReport.filter(item => {
            const status = (item.status || '').trim();
            const batchStatus = item.batch_no_status;
            // Unscanned should never be counted as processed
            if (status === 'Unscanned') return false;
            // Count as processed if status is 'Processed' or batch_no_status = 2
            return status === 'Processed' || batchStatus === 2 || batchStatus === '2';
        }).length;
        
        // Unscanned: items with status = 'Unscanned'
        const unscanned = filteredReport.filter(item => {
            const status = (item.status || '').trim();
            return status === 'Unscanned';
        }).length;
        
        // Employee Pending: items with status = 'Employee Pending'
        const employeePending = filteredReport.filter(item => {
            const status = (item.status || '').trim();
            return status === 'Employee Pending';
        }).length;
        
        // Department Pending: items with status = 'Department Pending'
        const departmentPending = filteredReport.filter(item => {
            const status = (item.status || '').trim();
            return status === 'Department Pending';
        }).length;
        
        // Total pending (Employee + Department)
        const totalPending = employeePending + departmentPending;
        
        // Calculate totals for weights and quantities
        const totals = filteredReport.reduce((acc, item) => {
            acc.totalQty += parseFloat(item.qty) || 0;
            acc.totalWeight += parseFloat(item.net_weight) || 0;
            acc.totalGrossWeight += parseFloat(item.gross_weight) || 0;
            return acc;
        }, { totalQty: 0, totalWeight: 0, totalGrossWeight: 0 });
        
        const total = filteredReport.length;
        
        return { 
            processed, 
            unscanned, 
            employeePending, 
            departmentPending, 
            totalPending,
            total,
            ...totals
        };
    }, [filteredReport]);
    
    const handleExport = async () => {
        try {
            // Fetch all data matching the current filters (same logic as Apply Filter)
            const params = buildFilterParams({ limit: 0, today_only: false });
            
            // Fetch the data with current filters using the API directly
            const exportData = await getDepartmentWisePendingReportApi(params);
            
            // Export the fetched data
            const csvData = formatCsvData(exportData || []);
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            if (link.href) {
                URL.revokeObjectURL(link.href);
            }
            link.href = URL.createObjectURL(blob);
            link.download = 'department_wise_pending_report.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Failed to export data. Please try again.');
        }
    };

    const buildFilterParams = useCallback((baseParams = {}, includeDates = true) => {
        const params = { ...baseParams };

        if (includeDates) {
            if (fromDate) {
                params.date_from = fromDate;
            }
            if (toDate) {
                params.date_to = toDate;
            }
        }

        if (departmentFilter) {
            params.department = departmentFilter;
        }
        if (workerFilter?.code) {
            params.worker = workerFilter.code;
        }
        if (statusFilter) {
            params.status = statusFilter;
        }
        if (searchTerm) {
            params.search = searchTerm;
        }

        if (params.limit === undefined) {
            params.limit = DEFAULT_LIMIT;
        }
        // Handle today_only flag
        if (params.today_only === undefined) {
            const hasDateFilter = params.date_from || params.date_to;
            const hasOtherFilter = params.department || params.worker || params.status || params.search;
            
            if (hasDateFilter) {
                // When date filters are present, explicitly set today_only to false
                // so backend uses the date_from/date_to filters instead
                params.today_only = false;
            } else if (hasOtherFilter) {
                // If user has applied other filters (status, department, worker, search) but no date filter,
                // don't restrict to today - show all matching records
                params.today_only = false;
            } else {
                // Only default to today_only if no filters are applied at all
                params.today_only = true;
            }
        }

        return params;
    }, [departmentFilter, workerFilter, statusFilter, searchTerm, fromDate, toDate]);

    const handleClearFilters = () => {
        setSearchTerm('');
        setDepartmentFilter(null);
        setWorkerFilter(null);
        setStatusFilter(null);
        setFromDate('');
        setToDate('');
        fetchReport({ limit: DEFAULT_LIMIT, today_only: true });
    };

    const handleApplyFilters = () => {
        // When applying filters, set limit to 0 to show all matching records
        // and explicitly set today_only to false to use the filter criteria
        const params = buildFilterParams({ limit: 0, today_only: false });
        fetchReport(params);
    };

    const handleLoadRecent = () => {
        const params = buildFilterParams({ limit: DEFAULT_LIMIT, today_only: true }, false);
        fetchReport(params);
        setFromDate('');
        setToDate('');
    };

    const handleLoadAll = () => {
        const params = buildFilterParams({ limit: 0, today_only: false });
        fetchReport(params);
    };


    return (
        <Box>
            <PageLoader loading={loading} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <ReportTitle>Department Wise Pending Report</ReportTitle>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleLoadRecent}
                        disabled={loading}
                    >
                        Load Recent
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleLoadAll}
                        disabled={loading}
                    >
                        Load All
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={loading || filteredReport.length === 0}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Filters Section - Now on Main Page */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Filters</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                    >
                        Clear All
                    </Button>
                </Box>
                <Grid container spacing={2} sx={{ width: '100%' }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={departments}
                            value={departmentFilter}
                            onChange={(event, newValue) => {
                                setDepartmentFilter(newValue);
                            }}
                            sx={{ minWidth: 250 }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Department" 
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select department..."
                                />
                            )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={workers}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            value={workerFilter}
                            onChange={(event, newValue) => {
                                setWorkerFilter(newValue);
                            }}
                            sx={{ minWidth: 250 }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Worker" 
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select worker..."
                                />
                            )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={['Processed', 'Employee Pending', 'Department Pending', 'Unscanned']}
                            value={statusFilter}
                            onChange={(event, newValue) => {
                                setStatusFilter(newValue);
                            }}
                            sx={{ minWidth: 250 }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Status" 
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select status..."
                                />
                            )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="From Date"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="To Date"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Button
                            variant="contained"
                            onClick={handleApplyFilters}
                            disabled={loading}
                            sx={{ mt: 0.5 }}
                            fullWidth
                        >
                            Apply Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Summary Statistics - Consolidated Totals */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Total Batches */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.total}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Batches
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Unscanned */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.unscanned}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Unscanned
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Employee Pending */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.employeePending}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Employee Pending
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Department Pending */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.departmentPending}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Department Pending
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Total Pending */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #ef5350 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.totalPending}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Pending
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Total Weight */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.totalWeight.toFixed(3)}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Weight (gms)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Total Qty */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #5d4037 0%, #8d6e63 100%)', color: 'white', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {summaryStats.totalQty.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Quantity
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p>Error: {error}</p>
            ) : (
                <DepartmentWisePendingReportTable reportData={filteredReport} loading={loading} />
            )}
        </Box>
    );
};

export default DepartmentWisePendingReportPage; 